import { useCallback, useEffect, useState } from "react";
import type {
  ShiftProductionForm,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import {
  decideShiftForm,
  getShiftForm,
  getShiftFormList,
  type FormListFilters,
} from "./shift-production-form-gateway";

export type ManagerView = "pending" | "accessible" | "completed";
export function useManagerFormWorkspace(initialFormId: string | null) {
  const [items, setItems] = useState<ShiftProductionFormListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<ManagerView, number>>({
    pending: 0,
    accessible: 0,
    completed: 0,
  });
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [selected, setSelected] = useState<ShiftProductionForm | null>(null);
  const [view, setView] = useState<ManagerView>(
    initialFormId ? "accessible" : "pending",
  );
  const [filters, setFilters] = useState<FormListFilters>({
    limit: 20,
    offset: 0,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const [list, pending, accessible, completed] = await Promise.all([
        getShiftFormList(view, filters),
        getShiftFormList("pending", { limit: 1 }),
        getShiftFormList("accessible", { limit: 1 }),
        getShiftFormList("completed", { limit: 1 }),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setCounts({
        pending: pending.total,
        accessible: accessible.total,
        completed: completed.total,
      });
      setPhase("ready");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Form listesi açılamadı.",
      );
      setPhase("error");
    }
  }, [filters, view]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (initialFormId) void open(initialFormId);
  }, [initialFormId]);

  const open = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      setSelected((await getShiftForm(id)).form);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Form açılamadı.");
    } finally {
      setBusy(false);
    }
  };
  const decide = async (
    action: "APPROVE",
    explanation: string,
    missingReason: string,
  ) => {
    if (!selected || !selected.canDecide) return;
    setBusy(true);
    setError(null);
    try {
      const response = await decideShiftForm(
        selected.id,
        selected.version,
        selected.revisionNumber,
        action,
        explanation,
        missingReason,
      );
      setSelected(response.form);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Karar kaydedilemedi.",
      );
    } finally {
      setBusy(false);
    }
  };
  const changeView = (next: ManagerView) => {
    setView(next);
    setFilters((current) => ({ ...current, offset: 0 }));
  };
  const changeFilters = (next: FormListFilters) =>
    setFilters({ ...next, limit: 20, offset: 0 });
  return {
    items,
    total,
    counts,
    phase,
    selected,
    view,
    filters,
    busy,
    error,
    open,
    decide,
    changeView,
    changeFilters,
    setFilters,
    reload: load,
    setSelected,
  };
}
